import { ChecklistModuleDefinition } from '../../types/due-diligence';

export const residentialStructureModule: ChecklistModuleDefinition = {
  code: 'residential_structure',
  name: 'Residential Structure',
  description: 'House inspection, systems, and condition',
  categories: [
    {
      code: 'general_info',
      name: 'General Information',
      items: [
        {
          code: 'structure_overview',
          title: 'Structure overview',
          description: 'Basic home information',
          isGroupTask: false,
          dataFields: [
            { key: 'year_built', label: 'Year Built', type: 'number' },
            { key: 'sqft', label: 'Square Footage', type: 'number', unit: 'sq ft' },
            { key: 'bedrooms', label: 'Bedrooms', type: 'number' },
            { key: 'bathrooms', label: 'Bathrooms', type: 'number' },
            { key: 'stories', label: 'Stories', type: 'number' },
            { key: 'style', label: 'Home Style', type: 'string' },
          ],
        },
        {
          code: 'construction_type',
          title: 'Construction type',
          description: 'Building construction and materials',
          isGroupTask: false,
          dataFields: [
            { key: 'foundation_type', label: 'Foundation Type', type: 'select', options: [
              { value: 'slab', label: 'Slab' },
              { value: 'crawl', label: 'Crawl Space' },
              { value: 'basement', label: 'Basement' },
              { value: 'pier', label: 'Pier and Beam' },
            ]},
            { key: 'frame_type', label: 'Frame Type', type: 'select', options: [
              { value: 'wood', label: 'Wood Frame' },
              { value: 'steel', label: 'Steel Frame' },
              { value: 'masonry', label: 'Masonry' },
              { value: 'log', label: 'Log' },
            ]},
            { key: 'exterior', label: 'Exterior Material', type: 'string' },
            { key: 'roof_type', label: 'Roof Type', type: 'string' },
          ],
        },
        {
          code: 'occupancy_status',
          title: 'Occupancy status',
          description: 'Current occupancy and condition',
          isGroupTask: false,
          dataFields: [
            { key: 'occupied', label: 'Currently Occupied', type: 'boolean' },
            { key: 'occupant_type', label: 'Occupant Type', type: 'select', options: [
              { value: 'owner', label: 'Owner Occupied' },
              { value: 'tenant', label: 'Tenant' },
              { value: 'vacant', label: 'Vacant' },
            ]},
            { key: 'vacancy_duration', label: 'Vacancy Duration', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'yellow', condition: 'Long-term vacancy (>6 months)' },
          ],
        },
      ],
    },
    {
      code: 'structural',
      name: 'Structural',
      items: [
        {
          code: 'foundation_inspection',
          title: 'Foundation inspection',
          description: 'Assess foundation condition',
          isGroupTask: false,
          dataFields: [
            { key: 'condition', label: 'Condition', type: 'select', options: [
              { value: 'excellent', label: 'Excellent' },
              { value: 'good', label: 'Good' },
              { value: 'fair', label: 'Fair' },
              { value: 'poor', label: 'Poor' },
            ]},
            { key: 'cracks_found', label: 'Cracks Found', type: 'boolean' },
            { key: 'crack_severity', label: 'Crack Severity', type: 'string' },
            { key: 'settling_signs', label: 'Signs of Settling', type: 'boolean' },
            { key: 'moisture_issues', label: 'Moisture Issues', type: 'boolean' },
            { key: 'repair_estimate', label: 'Repair Estimate', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Major foundation problems' },
            { flag: 'red', condition: 'Foundation issues requiring repair' },
            { flag: 'yellow', condition: 'Minor foundation concerns' },
          ],
        },
        {
          code: 'framing_structure',
          title: 'Framing/structure assessment',
          description: 'Evaluate structural integrity',
          isGroupTask: false,
          dataFields: [
            { key: 'condition', label: 'Condition', type: 'select', options: [
              { value: 'excellent', label: 'Excellent' },
              { value: 'good', label: 'Good' },
              { value: 'fair', label: 'Fair' },
              { value: 'poor', label: 'Poor' },
            ]},
            { key: 'sagging_floors', label: 'Sagging Floors', type: 'boolean' },
            { key: 'bowing_walls', label: 'Bowing Walls', type: 'boolean' },
            { key: 'termite_damage', label: 'Termite Damage', type: 'boolean' },
            { key: 'water_damage', label: 'Water Damage', type: 'boolean' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Major structural issues' },
            { flag: 'red', condition: 'Termite or water damage' },
          ],
        },
        {
          code: 'roof_inspection',
          title: 'Roof inspection',
          description: 'Evaluate roof condition and life',
          isGroupTask: false,
          dataFields: [
            { key: 'roof_age', label: 'Roof Age', type: 'number', unit: 'years' },
            { key: 'material', label: 'Material', type: 'string' },
            { key: 'condition', label: 'Condition', type: 'select', options: [
              { value: 'excellent', label: 'Excellent' },
              { value: 'good', label: 'Good' },
              { value: 'fair', label: 'Fair' },
              { value: 'poor', label: 'Poor' },
            ]},
            { key: 'remaining_life', label: 'Est. Remaining Life', type: 'number', unit: 'years' },
            { key: 'leaks_found', label: 'Leaks Found', type: 'boolean' },
            { key: 'replacement_cost', label: 'Replacement Cost', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Roof needs immediate replacement' },
            { flag: 'red', condition: 'Roof near end of life' },
            { flag: 'yellow', condition: 'Roof will need attention in 5 years' },
          ],
        },
      ],
    },
    {
      code: 'systems',
      name: 'Systems',
      items: [
        {
          code: 'hvac_inspection',
          title: 'HVAC system',
          description: 'Heating and cooling assessment',
          isGroupTask: false,
          dataFields: [
            { key: 'heating_type', label: 'Heating Type', type: 'string' },
            { key: 'cooling_type', label: 'Cooling Type', type: 'string' },
            { key: 'system_age', label: 'System Age', type: 'number', unit: 'years' },
            { key: 'condition', label: 'Condition', type: 'select', options: [
              { value: 'excellent', label: 'Excellent' },
              { value: 'good', label: 'Good' },
              { value: 'fair', label: 'Fair' },
              { value: 'poor', label: 'Poor' },
            ]},
            { key: 'last_service', label: 'Last Service', type: 'date' },
            { key: 'replacement_cost', label: 'Replacement Cost', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'HVAC needs replacement' },
            { flag: 'yellow', condition: 'HVAC aging, budget for replacement' },
          ],
        },
        {
          code: 'electrical_inspection',
          title: 'Electrical system',
          description: 'Electrical panel and wiring assessment',
          isGroupTask: false,
          dataFields: [
            { key: 'panel_amps', label: 'Panel Size', type: 'number', unit: 'amps' },
            { key: 'wiring_type', label: 'Wiring Type', type: 'select', options: [
              { value: 'copper', label: 'Copper' },
              { value: 'aluminum', label: 'Aluminum' },
              { value: 'knob_tube', label: 'Knob and Tube' },
              { value: 'mixed', label: 'Mixed' },
            ]},
            { key: 'grounded', label: 'Properly Grounded', type: 'boolean' },
            { key: 'gfci_present', label: 'GFCI Where Required', type: 'boolean' },
            { key: 'issues_found', label: 'Issues Found', type: 'array' },
            { key: 'upgrade_cost', label: 'Upgrade Cost', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Dangerous electrical issues' },
            { flag: 'red', condition: 'Knob and tube wiring' },
            { flag: 'yellow', condition: 'Panel undersized for needs' },
          ],
        },
        {
          code: 'plumbing_inspection',
          title: 'Plumbing system',
          description: 'Supply and drain plumbing assessment',
          isGroupTask: false,
          dataFields: [
            { key: 'supply_pipe_type', label: 'Supply Pipe Material', type: 'string' },
            { key: 'drain_pipe_type', label: 'Drain Pipe Material', type: 'string' },
            { key: 'water_heater_type', label: 'Water Heater Type', type: 'string' },
            { key: 'water_heater_age', label: 'Water Heater Age', type: 'number', unit: 'years' },
            { key: 'issues_found', label: 'Issues Found', type: 'array' },
            { key: 'lead_concerns', label: 'Lead Pipe Concerns', type: 'boolean' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Lead plumbing' },
            { flag: 'red', condition: 'Polybutylene pipe' },
            { flag: 'yellow', condition: 'Galvanized pipe showing age' },
          ],
        },
      ],
    },
    {
      code: 'interior',
      name: 'Interior',
      items: [
        {
          code: 'interior_condition',
          title: 'Overall interior condition',
          description: 'General interior assessment',
          isGroupTask: false,
          dataFields: [
            { key: 'condition', label: 'Overall Condition', type: 'select', options: [
              { value: 'excellent', label: 'Excellent - Move-in ready' },
              { value: 'good', label: 'Good - Minor updates' },
              { value: 'fair', label: 'Fair - Needs work' },
              { value: 'poor', label: 'Poor - Major renovation' },
            ]},
            { key: 'cosmetic_update_cost', label: 'Cosmetic Update Cost', type: 'number', unit: 'dollars' },
          ],
        },
        {
          code: 'kitchen_assessment',
          title: 'Kitchen condition',
          description: 'Kitchen evaluation',
          isGroupTask: false,
          dataFields: [
            { key: 'condition', label: 'Condition', type: 'select', options: [
              { value: 'excellent', label: 'Excellent' },
              { value: 'good', label: 'Good' },
              { value: 'fair', label: 'Fair' },
              { value: 'poor', label: 'Poor' },
            ]},
            { key: 'appliance_ages', label: 'Appliance Ages', type: 'string' },
            { key: 'cabinets_condition', label: 'Cabinets Condition', type: 'string' },
            { key: 'counters_condition', label: 'Counters Condition', type: 'string' },
            { key: 'update_cost', label: 'Update Cost', type: 'number', unit: 'dollars' },
          ],
        },
        {
          code: 'bathroom_assessment',
          title: 'Bathroom condition',
          description: 'Bathroom evaluation',
          isGroupTask: false,
          dataFields: [
            { key: 'condition', label: 'Overall Condition', type: 'select', options: [
              { value: 'excellent', label: 'Excellent' },
              { value: 'good', label: 'Good' },
              { value: 'fair', label: 'Fair' },
              { value: 'poor', label: 'Poor' },
            ]},
            { key: 'water_damage_signs', label: 'Water Damage Signs', type: 'boolean' },
            { key: 'ventilation_adequate', label: 'Ventilation Adequate', type: 'boolean' },
            { key: 'update_cost', label: 'Update Cost', type: 'number', unit: 'dollars' },
          ],
        },
        {
          code: 'flooring_assessment',
          title: 'Flooring condition',
          description: 'Floor covering assessment',
          isGroupTask: false,
          dataFields: [
            { key: 'flooring_types', label: 'Flooring Types', type: 'array' },
            { key: 'condition', label: 'Overall Condition', type: 'string' },
            { key: 'replacement_cost', label: 'Replacement Cost', type: 'number', unit: 'dollars' },
          ],
        },
      ],
    },
    {
      code: 'exterior',
      name: 'Exterior',
      items: [
        {
          code: 'siding_assessment',
          title: 'Siding/exterior condition',
          description: 'Exterior finish evaluation',
          isGroupTask: false,
          dataFields: [
            { key: 'siding_type', label: 'Siding Type', type: 'string' },
            { key: 'condition', label: 'Condition', type: 'select', options: [
              { value: 'excellent', label: 'Excellent' },
              { value: 'good', label: 'Good' },
              { value: 'fair', label: 'Fair' },
              { value: 'poor', label: 'Poor' },
            ]},
            { key: 'paint_condition', label: 'Paint Condition', type: 'string' },
            { key: 'repair_cost', label: 'Repair Cost', type: 'number', unit: 'dollars' },
          ],
        },
        {
          code: 'windows_doors',
          title: 'Windows and doors',
          description: 'Window and door assessment',
          isGroupTask: false,
          dataFields: [
            { key: 'window_type', label: 'Window Type', type: 'string' },
            { key: 'window_condition', label: 'Window Condition', type: 'string' },
            { key: 'door_condition', label: 'Door Condition', type: 'string' },
            { key: 'replacement_cost', label: 'Replacement Cost', type: 'number', unit: 'dollars' },
          ],
        },
        {
          code: 'deck_porch',
          title: 'Deck/porch condition',
          description: 'Deck and porch assessment',
          isGroupTask: false,
          dataFields: [
            { key: 'has_deck', label: 'Has Deck', type: 'boolean' },
            { key: 'deck_material', label: 'Deck Material', type: 'string' },
            { key: 'condition', label: 'Condition', type: 'string' },
            { key: 'structural_concerns', label: 'Structural Concerns', type: 'boolean' },
            { key: 'repair_cost', label: 'Repair Cost', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'Deck has structural issues' },
          ],
        },
      ],
    },
    {
      code: 'safety',
      name: 'Safety & Hazards',
      items: [
        {
          code: 'asbestos_check',
          title: 'Asbestos assessment',
          description: 'Check for asbestos-containing materials',
          isGroupTask: false,
          dataFields: [
            { key: 'asbestos_likely', label: 'Asbestos Likely', type: 'boolean' },
            { key: 'suspected_locations', label: 'Suspected Locations', type: 'array' },
            { key: 'testing_done', label: 'Testing Done', type: 'boolean' },
            { key: 'abatement_cost', label: 'Abatement Cost', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'Asbestos confirmed' },
            { flag: 'yellow', condition: 'Asbestos suspected' },
          ],
        },
        {
          code: 'lead_paint_check',
          title: 'Lead paint assessment',
          description: 'Check for lead-based paint (pre-1978)',
          isGroupTask: false,
          dataFields: [
            { key: 'pre_1978', label: 'Built Pre-1978', type: 'boolean' },
            { key: 'lead_likely', label: 'Lead Paint Likely', type: 'boolean' },
            { key: 'testing_done', label: 'Testing Done', type: 'boolean' },
            { key: 'remediation_cost', label: 'Remediation Cost', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'yellow', condition: 'Pre-1978 home, assume lead paint' },
          ],
        },
        {
          code: 'radon_check',
          title: 'Radon testing',
          description: 'Test for radon gas',
          isGroupTask: false,
          dataFields: [
            { key: 'testing_done', label: 'Testing Done', type: 'boolean' },
            { key: 'radon_level', label: 'Radon Level', type: 'number', unit: 'pCi/L' },
            { key: 'mitigation_needed', label: 'Mitigation Needed', type: 'boolean' },
            { key: 'mitigation_cost', label: 'Mitigation Cost', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'red', condition: 'High radon levels (>4 pCi/L)' },
            { flag: 'yellow', condition: 'Elevated radon (2-4 pCi/L)' },
          ],
        },
        {
          code: 'mold_check',
          title: 'Mold assessment',
          description: 'Check for mold or moisture issues',
          isGroupTask: false,
          dataFields: [
            { key: 'mold_found', label: 'Mold Found', type: 'boolean' },
            { key: 'mold_locations', label: 'Locations', type: 'array' },
            { key: 'moisture_source', label: 'Moisture Source', type: 'string' },
            { key: 'remediation_cost', label: 'Remediation Cost', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Significant mold problem' },
            { flag: 'red', condition: 'Mold present' },
          ],
        },
      ],
    },
    {
      code: 'professional_inspection',
      name: 'Professional Inspection',
      items: [
        {
          code: 'home_inspection',
          title: 'Professional home inspection',
          description: 'Full home inspection by licensed inspector',
          isGroupTask: false,
          dataFields: [
            { key: 'inspection_date', label: 'Inspection Date', type: 'date' },
            { key: 'inspector_name', label: 'Inspector', type: 'string' },
            { key: 'major_issues', label: 'Major Issues', type: 'array' },
            { key: 'minor_issues', label: 'Minor Issues', type: 'array' },
            { key: 'total_repair_estimate', label: 'Total Repair Estimate', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'big_red', condition: 'Major issues found' },
            { flag: 'red', condition: 'Significant issues found' },
            { flag: 'green', condition: 'Clean inspection' },
          ],
        },
        {
          code: 'specialist_inspections',
          title: 'Specialist inspections needed',
          description: 'Additional specialist inspections recommended',
          isGroupTask: false,
          dataFields: [
            { key: 'inspections_needed', label: 'Inspections Needed', type: 'multiselect', options: [
              { value: 'structural', label: 'Structural Engineer' },
              { value: 'septic', label: 'Septic' },
              { value: 'well', label: 'Well/Water' },
              { value: 'pest', label: 'Pest/Termite' },
              { value: 'roof', label: 'Roof' },
              { value: 'hvac', label: 'HVAC' },
              { value: 'electrical', label: 'Electrical' },
              { value: 'chimney', label: 'Chimney' },
              { value: 'pool', label: 'Pool/Spa' },
            ]},
            { key: 'total_inspection_cost', label: 'Total Inspection Cost', type: 'number', unit: 'dollars' },
          ],
        },
      ],
    },
  ],
};

